import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { getDestination, DESTINATIONS } from "../data/content";

function NotFound() {
  return (
    <main className="min-h-screen bg-navy grain flex flex-col items-center justify-center text-center px-6" data-testid="destination-notfound">
      <p className="overline text-gold">Not Found</p>
      <h1 className="font-serif text-ivory text-5xl mt-4">This destination is being curated.</h1>
      <Link to="/#destinations" className="btn-gold mt-10">View Destinations</Link>
    </main>
  );
}

export default function DestinationDetail() {
  const { slug } = useParams();
  const d = getDestination(slug);
  if (!d) return <NotFound />;

  const [lat, lng] = d.coords;
  const bbox = `${lng - 3},${lat - 2.2},${lng + 3},${lat + 2.2}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  const others = DESTINATIONS.filter((x) => x.slug !== d.slug).slice(0, 3);

  return (
    <main data-testid="destination-detail" className="bg-ivory">
      {/* Hero */}
      <section className="relative h-[100svh] overflow-hidden bg-navy">
        <motion.img
          src={d.hero} alt={d.name}
          initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.55)" }} />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[14vh]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="overline text-gold mb-6">{d.region} · {d.tag}</motion.p>
          <h1 className="font-serif text-ivory text-7xl md:text-[10vw] leading-[0.9]">
            <MaskLines lines={[d.name]} delay={0.4} />
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-ivory py-24 md:py-36">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-charcoal/50 mb-8">The Destination</p>
            <p className="font-serif text-3xl md:text-4xl text-navy leading-[1.3]">{d.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Highlights + Activities */}
      <section className="bg-navy grain py-24 md:py-36" data-testid="destination-highlights">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16">
          <Reveal>
            <p className="overline text-gold mb-8">Tour Highlights</p>
            <ul className="space-y-4">
              {d.highlights.map((h) => (
                <li key={h} className="flex items-start gap-4 border-b border-ivory/10 pb-4">
                  <span className="text-gold mt-1">✦</span>
                  <span className="font-serif text-2xl text-ivory">{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-gold mb-8">Activities</p>
            <div className="flex flex-wrap gap-3">
              {d.activities.map((a) => (
                <span key={a} className="border border-ivory/20 text-ivory/80 px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors">{a}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Itinerary timeline */}
      <section className="bg-ivory py-24 md:py-36" data-testid="destination-itinerary">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal className="mb-16">
            <p className="overline text-charcoal/50">Suggested Itinerary</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">A journey, day by day.</h2>
          </Reveal>
          <div className="relative border-l border-navy/15 ml-3">
            {d.itinerary.map((day, idx) => (
              <Reveal key={day.day} delay={idx * 0.05}>
                <div className="relative pl-10 pb-14">
                  <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-gold" />
                  <div className="flex items-baseline gap-4">
                    <p className="font-serif text-gold text-xl">Day {day.day}</p>
                    <h3 className="font-serif text-3xl text-navy">{day.title}</h3>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-6 mt-6">
                    {[["Morning", day.morning], ["Afternoon", day.afternoon], ["Evening", day.evening]].map(([label, txt]) => txt ? (
                      <div key={label}>
                        <p className="overline text-charcoal/45 mb-2">{label}</p>
                        <p className="text-charcoal/70 text-[15px] leading-relaxed">{txt}</p>
                      </div>
                    ) : null)}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-ivory pb-24 md:pb-36" data-testid="destination-gallery">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-4">
          {d.gallery.map((src, idx) => (
            <Reveal key={idx} delay={idx * 0.06}>
              <div className="overflow-hidden grain h-[320px] group">
                <img src={src} alt={`${d.name} ${idx + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Map */}
      <section className="bg-navy grain py-24 md:py-36" data-testid="destination-map">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-10 flex items-center gap-3">
            <MapPin className="text-gold" size={18} />
            <p className="overline text-gold">Where in the world · {d.name}</p>
          </Reveal>
          <Reveal>
            <div className="relative border border-gold/30 overflow-hidden h-[420px]" style={{ filter: "grayscale(0.6) contrast(1.05)" }}>
              <iframe title={`Map of ${d.name}`} src={mapSrc} className="w-full h-full" loading="lazy" style={{ border: 0 }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Enquiry CTA */}
      <section className="relative py-32 md:py-44 overflow-hidden bg-navy" data-testid="destination-cta">
        <img src={d.hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.74)" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[20ch] mx-auto leading-[1.05]">Design your journey to {d.name}.</h2>
            <Link to={`/enquire?type=prive&destination=${encodeURIComponent(d.name)}`} className="btn-gold mt-12" data-testid="destination-enquire-cta">Create My Bespoke Journey <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* Continue exploring */}
      <section className="bg-ivory py-24">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <p className="overline text-charcoal/50 mb-10">Continue Exploring</p>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/destinations/${o.slug}`} className="group block" data-testid={`related-${o.slug}`}>
                <div className="relative h-[300px] overflow-hidden grain">
                  <img src={o.thumb} alt={o.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.7), transparent 60%)" }} />
                  <div className="absolute bottom-0 p-6">
                    <p className="overline text-gold/80">{o.region}</p>
                    <h3 className="font-serif text-3xl text-ivory mt-1">{o.name}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
